import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { TrashIcon, LockClosedIcon, CalendarIcon, ExclamationTriangleIcon, MagnifyingGlassIcon, FunnelIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { Student } from '../types/student';
import TableRowSkeleton from '../components/TableRowSkeleton';
import type { FormEvent, ChangeEvent } from 'react';
import { apiUrl, authFetch, authService } from '../utils/api';
import { useUser } from '../contexts/UserContext';
import { P } from '../types/assistantPrivileges';

interface SortConfig {
  key: keyof Student | null;
  direction: 'asc' | 'desc';
}

interface Grade {
  id: string;
  name: string;
  description?: string;
  level?: number;
  active?: boolean;
}

interface Filters {
  userName: string;
  gradeId: string;
  email: string;
  phonenumber: string;
  registrationDateStart: string;
  registrationDateEnd: string;
  status: string;
}

interface SearchParams {
  name: string;
  phonenumber: string;
  email: string;
}

interface StudentFormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  gradeId: string;
  mobile: string;
  parentPhone: string;
  governorate: string;
  center: string;
}

const Students = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isTeacher, isAssistant, getTeacherId, isSaeedAdmin, hasPrivilege } = useUser();
  const teacherUser = isTeacher();
  const assistantUser = isAssistant();
  const canAddStudents = !teacherUser && hasPrivilege(P.Student_Add);
  const canEditStudents = !teacherUser && hasPrivilege(P.Student_Edit);
  const canDeleteStudents = !teacherUser && hasPrivilege(P.Student_Delete);
  const canViewStudentCounts = isSaeedAdmin();
  const [showAddForm, setShowAddForm] = useState(false);
  const [students, setStudents] = useState<Student[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: null,
    direction: 'asc'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalStudents, setTotalStudents] = useState(0);

  const [grades, setGrades] = useState<Grade[]>([]);
  const [isLoadingGrades, setIsLoadingGrades] = useState(false);

  const [filters, setFilters] = useState<Filters>({
    userName: '',
    gradeId: '',
    email: '',
    phonenumber: '',
    registrationDateStart: '',
    registrationDateEnd: '',
    status: '',
  });

  const [appliedFilters, setAppliedFilters] = useState<Filters>({
    userName: '',
    gradeId: '',
    email: '',
    phonenumber: '',
    registrationDateStart: '',
    registrationDateEnd: '',
    status: '',
  });

  const [searchParams, setSearchParams] = useState<SearchParams>({
    name: '',
    phonenumber: '',
    email: ''
  });

  const [appliedSearchParams, setAppliedSearchParams] = useState<SearchParams>({
    name: '',
    phonenumber: '',
    email: ''
  });

  const [studentToDelete, setStudentToDelete] = useState<Student | null>(null);
  const [studentToBlock, setStudentToBlock] = useState<Student | null>(null);
  const [deletedStudentName, setDeletedStudentName] = useState<string | null>(null);
  const [showDeleteSuccessModal, setShowDeleteSuccessModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBlocking, setIsBlocking] = useState(false);
  const [showRegDatePicker, setShowRegDatePicker] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const datePickerRef = useRef<HTMLDivElement | null>(null);
  const [regDateMonth, setRegDateMonth] = useState(new Date().getMonth());
  const [regDateYear, setRegDateYear] = useState(new Date().getFullYear());

  const [formData, setFormData] = useState<StudentFormData>({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    gradeId: '',
    mobile: '',
    parentPhone: '',
    governorate: '',
    center: '',
  });

  const navigationMessage = location.state?.message;

  /** Map Grade API level to backend AcademicYear (GradeEnum) for AdminStudent/add */
  const levelToAcademicYear: Record<number, string> = {
    1: 'Primary1', 2: 'Primary2', 3: 'Primary3', 4: 'Primary4', 5: 'Primary5', 6: 'Primary6',
    7: 'Prep1', 8: 'Prep2', 9: 'Prep3',
    10: 'Secondary1', 11: 'Secondary2', 12: 'Secondary3',
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setShowRegDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (teacherUser) return;
    if (assistantUser && !canAddStudents) return;
    const fetchGrades = async () => {
      setIsLoadingGrades(true);
      try {
        const res = await authFetch(apiUrl('Grade'), {
          method: 'GET',
          headers: {
            accept: '*/*',
            Authorization: `Bearer ${authService.getToken()}`
          }
        });
        if (!res.ok) return;
        const json = await res.json();
        if (json.success && Array.isArray(json.data)) {
          setGrades(
            json.data.map((g: any) => ({
              id: g.id ?? g.Id,
              name: g.name ?? g.Name ?? '',
              description: g.description ?? g.Description,
              level: g.level ?? g.Level,
              active: g.active ?? g.Active
            }))
          );
        }
      } catch {
        /* ignore */
      } finally {
        setIsLoadingGrades(false);
      }
    };
    fetchGrades();
  }, [teacherUser, assistantUser, canAddStudents]);

  const fetchStudents = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      let url;
      
      // Check if user is a teacher
      if (isTeacher()) {
        const teacherId = getTeacherId();
        if (!teacherId) {
          throw new Error('Teacher ID not found');
        }
        
        // For teachers, use the teacher-specific API
        url = apiUrl(`Teacher/${teacherId}/students?pageNumber=${currentPage}&pageSize=${pageSize}`);
      } else {
        // For moderators, use the existing logic
        const hasSearchFilters = appliedSearchParams.name || appliedSearchParams.phonenumber || appliedSearchParams.email || 
                               appliedFilters.gradeId || appliedFilters.registrationDateStart || 
                               appliedFilters.registrationDateEnd || appliedFilters.status;
        
        if (hasSearchFilters) {
          // Use search endpoint when filters are applied
          const params = new URLSearchParams();
          
          // Add search parameters if they exist
          if (appliedSearchParams.name) params.append('Name', appliedSearchParams.name);
          if (appliedSearchParams.phonenumber) params.append('PhoneNumber', appliedSearchParams.phonenumber);
          if (appliedSearchParams.email) params.append('Email', appliedSearchParams.email);
          
          // Add filter parameters
          if (appliedFilters.gradeId) params.append('GradeId', appliedFilters.gradeId);
          if (appliedFilters.registrationDateStart) params.append('FromDate', appliedFilters.registrationDateStart);
          if (appliedFilters.registrationDateEnd) params.append('ToDate', appliedFilters.registrationDateEnd);
          if (appliedFilters.status) params.append('Active', (appliedFilters.status === 'active').toString());
          
          // Add pagination parameters
          params.append('PageNumber', currentPage.toString());
          params.append('PageSize', pageSize.toString());

          url = apiUrl(`AdminStudent/Studentsearch?${params.toString()}`);
        } else {
          url = apiUrl(`AdminStudent/all?pageNumber=${currentPage}&pageSize=${pageSize}`);
        }
      }

      console.log('Fetching students from URL:', url);

      const response = await authFetch(url, {
        method: 'GET',
        headers: {
          'accept': '*/*',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });

      if (!response.ok) {
        console.error('API response not OK:', response.status, response.statusText);
        throw new Error(`Failed to fetch students: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      console.log('API Response:', result);
      
      if (!result.success) {
        console.error('API returned success: false:', result);
        throw new Error(result.message || 'Failed to fetch students');
      }

      // Log the structure of the response
      console.log('Response data structure:', {
        hasData: !!result.data,
        totalStudents: result.data?.totalStudents,
        totalPages: result.data?.totalPages,
        studentsLength: result.data?.students?.length
      });

      // If no data property exists at all
      if (!result.data) {
        console.warn('No data property in response');
        setStudents([]);
        setTotalPages(0);
        setTotalCount(0);
        setTotalStudents(0);
        return;
      }

      // Handle different response structures for teachers vs moderators
      let studentsArray, totalStudents, totalCount, totalPages;
      
      if (isTeacher()) {
        // Teacher API response structure
        studentsArray = result.data.students || [];
        totalStudents = result.data.totalCount;
        totalCount = result.data.totalCount;
        totalPages = result.data.totalPages;
      } else {
        // Moderator / Search API: totalStudents, totalPages, students (and optionally totalCount)
        studentsArray = result.data.students || [];
        totalStudents = result.data.totalStudents ?? result.data.totalCount ?? 0;
        totalCount = totalStudents;
        totalPages = result.data.totalPages ?? 1;
      }

      // Map the response data to include status and correct field names
      const studentsWithStatus = studentsArray.map((student: any) => {
        if (isTeacher()) {
          // Map teacher API response fields to expected student object structure
          return {
            id: student.id,
            userName: student.studentName,
            email: student.studentEmail,
            phonenumber: student.phoneNumber,
            academicYear: student.academicYear,
            registrationDate: student.linkedDate,
            status: student.isActive ? 'active' : 'blocked',
            active: student.isActive,
            gradeName: student.gradeName,
            governorate: student.governorate
          };
        } else {
          // Moderator API: preserve gradeName, governorate, and map status
          return {
            ...student,
            gradeName: student.gradeName,
            governorate: student.governorate,
            status: student.isActive !== undefined ? (student.isActive ? 'active' : 'blocked') : (student.active ? 'active' : 'blocked')
          };
        }
      });
      
      setStudents(studentsWithStatus);
      setTotalPages(totalPages);
      setTotalCount(totalCount);
      setTotalStudents(totalStudents);
      
      // Log final state updates
      console.log('Updated state:', {
        studentsCount: studentsWithStatus.length,
        totalPages,
        totalStudents
      });

    } catch (error) {
      console.error('Error fetching students:', error);
      setError('Failed to load students. Please try again later.');
      setStudents([]);
      setTotalPages(0);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, isTeacher, getTeacherId, appliedSearchParams, appliedFilters]);

  useEffect(() => {
    fetchStudents();

    if (navigationMessage) {
      setSuccessMessage(navigationMessage);
      const timer = setTimeout(() => {
        setSuccessMessage(null);
        window.history.replaceState({}, document.title);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [currentPage, navigationMessage, fetchStudents]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleSort = (key: keyof Student) => {
    setSortConfig((prevConfig) => {
      const direction = prevConfig?.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc';
      return { key, direction };
    });
  };

  const handleFilterChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSearchParams(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleApplyFilters = () => {
    // Create the new filter values
    const newAppliedFilters = { ...filters };
    const newAppliedSearchParams = { ...searchParams };

    // Use these values directly in the API call
    const params = new URLSearchParams();
    
    // Add search parameters if they exist
    if (searchParams.name) params.append('Name', searchParams.name);
    if (searchParams.phonenumber) params.append('PhoneNumber', searchParams.phonenumber);
    if (searchParams.email) params.append('Email', searchParams.email);
    
    // Add filter parameters
    if (filters.gradeId) params.append('GradeId', filters.gradeId);
    if (filters.registrationDateStart) params.append('FromDate', filters.registrationDateStart);
    if (filters.registrationDateEnd) params.append('ToDate', filters.registrationDateEnd);
    if (filters.status) params.append('Active', (filters.status === 'active').toString());
    
    // Add pagination parameters
    params.append('PageNumber', '1');
    params.append('PageSize', pageSize.toString());

    // Update the states
    setAppliedFilters(newAppliedFilters);
    setAppliedSearchParams(newAppliedSearchParams);
    setCurrentPage(1);

    // Make the API call directly with current values
    setIsLoading(true);
    setError(null);

    const hasSearchFilters = searchParams.name || searchParams.phonenumber || searchParams.email || 
                           filters.gradeId || filters.registrationDateStart || 
                           filters.registrationDateEnd || filters.status;

    const url = hasSearchFilters 
      ? apiUrl(`AdminStudent/Studentsearch?${params.toString()}`)
      : apiUrl(`AdminStudent/all?pageNumber=1&pageSize=${pageSize}`);

    authFetch(url, {
      method: 'GET',
      headers: {
        'accept': '*/*',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authService.getToken()}`
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to fetch students: ${response.status} ${response.statusText}`);
        }
        return response.json();
      })
      .then(result => {
        if (!result.success) {
          throw new Error(result.message || 'Failed to fetch students');
        }

        if (!result.data) {
          setStudents([]);
          setTotalPages(0);
          setTotalCount(0);
          setTotalStudents(0);
          return;
        }

        const studentsArray = result.data.students || [];
        const totalStudents = result.data.totalStudents ?? result.data.totalCount ?? 0;
        const totalPages = result.data.totalPages ?? 1;

        const studentsWithStatus = studentsArray.map((student: any) => ({
          ...student,
          gradeName: student.gradeName,
          governorate: student.governorate,
          status: student.isActive !== undefined ? (student.isActive ? 'active' : 'blocked') : (student.active ? 'active' : 'blocked')
        }));
        
        setStudents(studentsWithStatus);
        setTotalPages(totalPages);
        setTotalCount(totalStudents);
        setTotalStudents(totalStudents);
      })
      .catch(error => {
        console.error('Error fetching students:', error);
        setError('Failed to load students. Please try again later.');
        setStudents([]);
        setTotalPages(0);
        setTotalCount(0);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleResetFilters = () => {
    setFilters({
      userName: '',
      gradeId: '',
      email: '',
      phonenumber: '',
      registrationDateStart: '',
      registrationDateEnd: '',
      status: '',
    });
    setSearchParams({
      name: '',
      phonenumber: '',
      email: ''
    });
    setAppliedFilters({
      userName: '',
      gradeId: '',
      email: '',
      phonenumber: '',
      registrationDateStart: '',
      registrationDateEnd: '',
      status: '',
    });
    setAppliedSearchParams({
      name: '',
      phonenumber: '',
      email: ''
    });
    setCurrentPage(1);
    fetchStudents();
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleDeleteStudent = async (id: string) => {
    setIsDeleting(true);
    setError(null);
    const deletedName = studentToDelete?.userName || 'الطالب';
    try {
      const response = await authFetch(apiUrl(`AdminStudent/delete/${id}`), {
        method: 'DELETE',
        headers: {
          'accept': '*/*',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      const data = await response.json();
      if (response.ok && data.success !== false) {
        setStudents(prev => prev.filter(student => student.id !== id));
        setTotalCount(prev => Math.max(0, prev - 1));
        setTotalStudents(prev => Math.max(0, prev - 1));
        setStudentToDelete(null);
        setDeletedStudentName(deletedName);
        setShowDeleteSuccessModal(true);
        setTimeout(() => {
          setShowDeleteSuccessModal(false);
          setDeletedStudentName(null);
        }, 2500);
      } else {
        setError(data.message || data.data || 'فشل حذف الطالب');
      }
    } catch (error) {
      setError('فشل حذف الطالب. يرجى المحاولة مرة أخرى.');
    } finally {
      setIsDeleting(false);
      setIsLoading(false);
    }
  };

  const handleToggleStatus = async (student: Student) => {
    setIsBlocking(true);
    setError(null);
    try {
      const response = await authFetch(
        apiUrl(`AdminStudent/updatestatus/${student.id}?Studentid=${student.id}`),
        {
          method: 'PUT',
          headers: {
            'accept': '*/*',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        }
      );

      const result = await response.json();

      if (result.success) {
        // Update the local state with the new status
        setStudents(prev => 
          prev.map(s => 
            s.id === student.id 
                ? { ...s, status: s.status === 'active' ? 'blocked' : 'active', active: s.status === 'blocked' } 
              : s
          )
        );
        setStudentToBlock(null);
        setSuccessMessage(result.data || 'Student status updated successfully');
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccessMessage(null);
        }, 3000);
      } else {
        throw new Error(result.message || 'Failed to update student status');
      }
    } catch (error) {
      console.error('Error updating student status:', error);
      setError('Failed to update student status. Please try again later.');
    } finally {
      setIsBlocking(false);
    }
  };

  // Calendar helper functions
  const getMonthName = (month: number, year: number) => {
    return new Date(year, month).toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  const getDaysInMonth = (month: number, year: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month: number, year: number) => {
    return new Date(year, month, 1).getDay();
  };

  const handleDateSelect = (date: string) => {
    const selectedDate = new Date(date);
    const formattedDate = selectedDate.toISOString().split('T')[0];

    if (!filters.registrationDateStart || filters.registrationDateEnd) {
      // Start a new range
      setFilters({
        ...filters,
        registrationDateStart: formattedDate,
        registrationDateEnd: ''
      });
    } else {
      // Complete the range
      const startDate = new Date(filters.registrationDateStart);
      const endDate = new Date(formattedDate);
      
      if (endDate < startDate) {
        // If end date is before start date, swap them
        setFilters({
          ...filters,
          registrationDateStart: formattedDate,
          registrationDateEnd: filters.registrationDateStart
        });
      } else {
        setFilters({
          ...filters,
          registrationDateEnd: formattedDate
        });
      }
      setShowRegDatePicker(false);
    }
  };

  const isDateSelected = (dateString: string) => {
    return dateString === filters.registrationDateStart || dateString === filters.registrationDateEnd;
  };

  const isDateInRange = (dateString: string) => {
    if (!filters.registrationDateStart || !filters.registrationDateEnd) return false;
    
    const date = new Date(dateString);
    const start = new Date(filters.registrationDateStart);
    const end = new Date(filters.registrationDateEnd);
    
    return date >= start && date <= end;
  };

  // Calendar rendering section
  const renderCalendar = () => {
    const days = Array.from({ length: getDaysInMonth(regDateMonth, regDateYear) }, (_, i) => {
      const date = new Date(regDateYear, regDateMonth, i + 1);
      const dateString = date.toISOString().split('T')[0];
      const isSelected = isDateSelected(dateString);
      const isInRange = isDateInRange(dateString);
      
      return (
        <button
          key={i}
          onClick={() => handleDateSelect(dateString)}
          className={`
            relative h-10 w-10 flex items-center justify-center text-sm
            ${isSelected 
              ? 'bg-indigo-600 text-white hover:bg-indigo-700 font-semibold z-10' 
              : isInRange
                ? 'bg-indigo-50 text-indigo-600'
                : 'text-gray-900 hover:bg-gray-100'
            }
            ${dateString === filters.registrationDateStart ? 'rounded-l-full' : ''}
            ${dateString === filters.registrationDateEnd ? 'rounded-r-full' : ''}
            ${!isSelected && !isInRange ? 'rounded-full' : ''}
          `}
        >
          {i + 1}
        </button>
      );
    });

    const emptyCells = Array.from({ length: getFirstDayOfMonth(regDateMonth, regDateYear) }, (_, i) => (
      <div key={`empty-${i}`} className="h-10 w-10" />
    ));

    return [...emptyCells, ...days];
  };

  // Date picker dropdown content
  const renderDatePicker = () => (
    <div className="absolute z-10 mt-1 w-[320px] bg-white rounded-lg shadow-lg border border-gray-200">
      <div className="p-4">
        {/* Calendar Header */}
        <div className="flex items-center justify-between mb-4">
          <button 
            type="button"
            onClick={handleRegDatePrevMonth}
            className="p-1 hover:bg-gray-100 rounded-full text-gray-600 hover:text-gray-900"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="text-sm font-medium text-gray-900">
            {getMonthName(regDateMonth, regDateYear)}
          </span>
          <button 
            type="button"
            onClick={handleRegDateNextMonth}
            className="p-1 hover:bg-gray-100 rounded-full text-gray-600 hover:text-gray-900"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Calendar Grid */}
        <div className="mb-2 grid grid-cols-7 gap-0">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
            <div key={day} className="h-10 w-10 flex items-center justify-center text-xs font-medium text-gray-500">
              {day}
            </div>
          ))}
          {renderCalendar()}
        </div>

        {/* Selected Range Display */}
        {(filters.registrationDateStart || filters.registrationDateEnd) && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {filters.registrationDateStart && (
                  <span>
                    From: {new Date(filters.registrationDateStart).toLocaleDateString()}
                  </span>
                )}
                {filters.registrationDateEnd && (
                  <span className="ml-2">
                    To: {new Date(filters.registrationDateEnd).toLocaleDateString()}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  setFilters({
                    ...filters,
                    registrationDateStart: '',
                    registrationDateEnd: ''
                  });
                }}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                Clear
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const formatDateDisplay = (startDate: string, endDate: string) => {
    if (!startDate && !endDate) return '';
    
    const formatDate = (date: string) => {
      const d = new Date(date);
      return d.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric'
      });
    };

    if (startDate && !endDate) return formatDate(startDate);
    if (!startDate && endDate) return `until ${formatDate(endDate)}`;
    return `${formatDate(startDate)} to ${formatDate(endDate)}`;
  };

  const handleRegDatePrevMonth = () => {
    if (regDateMonth === 0) {
      setRegDateMonth(11);
      setRegDateYear(regDateYear - 1);
    } else {
      setRegDateMonth(regDateMonth - 1);
    }
  };

  const handleRegDateNextMonth = () => {
    if (regDateMonth === 11) {
      setRegDateMonth(0);
      setRegDateYear(regDateYear + 1);
    } else {
      setRegDateMonth(regDateMonth + 1);
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const phoneRegex = /^01[0-9]{9}$/;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (!validateEmail(formData.email)) {
      setError('أدخل بريدًا إلكترونيًا صحيحًا.');
      return;
    }
    if (!phoneRegex.test(formData.mobile.trim())) {
      setError('رقم هاتف الطالب غير صالح. يجب أن يكون 11 رقمًا ويبدأ بـ 01.');
      return;
    }
    if (!phoneRegex.test(formData.parentPhone.trim())) {
      setError('رقم هاتف ولي الأمر غير صالح. يجب أن يكون 11 رقمًا ويبدأ بـ 01.');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('كلمة المرور وتأكيد كلمة المرور غير متطابقتين.');
      return;
    }
    if (formData.password.length < 6) {
      setError('كلمة المرور يجب أن تكون 6 أحرف على الأقل.');
      return;
    }

    const g = grades.find((x) => x.id === formData.gradeId);
    const level = g?.level ?? 0;
    const academicYear = levelToAcademicYear[level];
    if (!academicYear) {
      setError('اختر صفًا دراسيًا صحيحًا.');
      return;
    }

    try {
      setIsLoading(true);

      const requestBody = {
        userName: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        phoneNumber: formData.mobile.trim(),
        parentPhone: formData.parentPhone.trim(),
        academicYear,
        governorate: formData.governorate.trim() || undefined,
        center: formData.center.trim() || undefined,
      };

      const response = await authFetch(apiUrl('AdminStudent/add'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          accept: '*/*',
          Authorization: `Bearer ${authService.getToken()}`,
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();

      if (!response.ok) {
        if (result.errors) {
          const msgs = Object.values(result.errors).flat();
          throw new Error(Array.isArray(msgs) ? msgs.join(', ') : String(msgs));
        }
        throw new Error(result.message || 'فشل إضافة الطالب.');
      }

      if (!result.success) {
        throw new Error(result.message || 'فشل إضافة الطالب.');
      }

      setSuccessMessage(result.message || 'تمت إضافة الطالب بنجاح.');
      setTimeout(() => setSuccessMessage(null), 3000);
      await fetchStudents();
      setShowAddForm(false);
      setFormData({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        gradeId: '',
        mobile: '',
        parentPhone: '',
        governorate: '',
        center: '',
      });
    } catch (err) {
      console.error('Add student error:', err);
      setError(err instanceof Error ? err.message : 'حدث خطأ.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Update this function to navigate directly without making an unnecessary API call
  const handleViewStudentDetails = (studentId: string) => {
    // Navigate directly to student details page without making a validation API call
    navigate(`/students/${studentId}`);
  };

  // Apply sorting to the current page of data
  const sortedStudents = useMemo(() => {
    if (!sortConfig || !sortConfig.key) return students;
    
    return [...students].sort((a, b) => {
      const key = sortConfig.key as keyof Student;
      const direction = sortConfig.direction;
      
      const aValue = a[key];
      const bValue = b[key];
      
      if (aValue < bValue) {
        return direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [students, sortConfig]);

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      {/* Main Header */}
      <div className="bg-white shadow">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-dark">الطلاب</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        {/* Error Alert */}
        {error && (
          <div className="mb-6 rounded-lg bg-red-50 p-4">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
              <div className="mr-3">
                <h3 className="text-sm font-medium text-red-800">خطأ</h3>
                <p className="mt-1 text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Success Alert */}
        {successMessage && (
          <div className="fixed top-4 left-4 z-50 animate-fade-in-down">
            <div className="rounded-lg bg-white p-4 shadow-lg ring-1 ring-black ring-opacity-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="mr-3">
                  <p className="text-sm font-medium text-gray-900">{successMessage}</p>
                </div>
                <div className="mr-4 flex-shrink-0">
                  <button
                    onClick={() => setSuccessMessage(null)}
                    className="inline-flex text-gray-400 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filters Card - Only show for moderators and assistants */}
        {!isTeacher() && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-medium text-gray-900 flex items-center">
                <FunnelIcon className="h-5 w-5 mr-2 text-gray-500" />
                البحث والتصفية
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">اسم الطالب</label>
                <div className="relative">
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    name="name"
                    value={searchParams.name}
                    onChange={handleSearchChange}
                    placeholder="البحث بالاسم"
                    className="block w-full pr-10 pl-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    lang="ar"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">البريد الإلكتروني</label>
                <div className="relative">
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <input
                    type="email"
                    name="email"
                    value={searchParams.email}
                    onChange={handleSearchChange}
                    placeholder="البحث بالبريد الإلكتروني"
                    className="block w-full pr-10 pl-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    lang="ar"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">رقم الهاتف</label>
                <div className="relative">
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <input
                    type="tel"
                    name="phonenumber"
                    value={searchParams.phonenumber}
                    onChange={handleSearchChange}
                    placeholder="البحث برقم الهاتف"
                    className="block w-full pr-10 pl-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    lang="ar"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">الصف</label>
                <select
                  name="gradeId"
                  value={filters.gradeId}
                  onChange={handleFilterChange}
                  disabled={isLoadingGrades}
                  className="block w-full pr-3 pl-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md disabled:opacity-50"
                  lang="ar"
                >
                  <option value="">جميع الصفوف</option>
                  {grades.map((g) => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">الحالة</label>
                <select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="block w-full pr-3 pl-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                  lang="ar"
                >
                  <option value="">جميع الحالات</option>
                  <option value="active">نشط</option>
                  <option value="blocked">محظور</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">تاريخ التسجيل</label>
                <div className="relative" ref={datePickerRef}>
                  <button
                    type="button"
                    onClick={() => setShowRegDatePicker(!showRegDatePicker)}
                    className="w-full bg-white px-3 py-2 border border-gray-300 rounded-md shadow-sm text-left focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <div className="flex items-center">
                      <CalendarIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className={filters.registrationDateStart || filters.registrationDateEnd ? 'text-gray-900' : 'text-gray-400'}>
                        {formatDateDisplay(filters.registrationDateStart, filters.registrationDateEnd) || 'Select date range'}
                      </span>
                    </div>
                  </button>
                  
                  {/* Date Picker Dropdown */}
                  {showRegDatePicker && renderDatePicker()}
                </div>
              </div>
            </div>
            
            {/* Filter Action Buttons - Moved to bottom */}
            <div className="flex items-center justify-start space-x-reverse space-x-3 mt-6">
              <button
                onClick={handleResetFilters}
                className="px-3 py-2 text-gray-600 hover:text-gray-900 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-200 mr-3"
              >
               إعادة تعيين
              </button>
              <button
                onClick={handleApplyFilters}
                className="px-3 py-2 text-sm bg-indigo-50 text-indigo-600 rounded-md hover:bg-indigo-100 transition-colors duration-200"
              >
                تطبيق التصفية
              </button>
            </div>
          </div>
        </div>
        )}

      {/* Students Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <span className="font-medium">
              {canViewStudentCounts ? (
                <>
                  العدد الكلي: {totalCount}
                  {totalStudents && totalStudents !== totalCount && (
                    <span className="text-sm text-gray-500 mr-2">(إجمالي الطلاب: {totalStudents})</span>
                  )}
                </>
              ) : (
                'قائمة الطلاب'
              )}
            </span>
            {canAddStudents && (
              <button
                onClick={() => { setError(null); setShowAddForm(true); }}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
                إضافة طالب جديد
              </button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-50">
                {[
                  { key: 'userName', label: 'الاسم' },
                  { key: 'gradeName', label: 'الصف' },
                  { key: 'governorate', label: 'المحافظة' },
                  { key: 'email', label: 'البريد الإلكتروني' },
                  { key: 'phonenumber', label: 'رقم الهاتف' },
                  { key: 'registrationDate', label: 'تاريخ التسجيل' },
                  { key: 'status', label: 'الحالة' },
                  { key: 'actions', label: 'الإجراءات' }
                ].map(({ key, label }) => (
                  <th
                    key={key}
                    scope="col" 
                    className={`px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider ${
                      key === 'actions' ? 'text-left' : ''
                    }`}
                  >
                    {key !== 'actions' ? (
                      <button
                        className="group inline-flex items-center space-x-reverse space-x-1 hover:text-gray-900"
                        onClick={() => key !== 'actions' && handleSort(key as keyof Student)}
                      >
                        <span>{label}</span>
                        {sortConfig?.key === key && (
                          <svg 
                            className={`h-4 w-4 transition-transform ${
                              sortConfig.direction === 'asc' ? 'rotate-0' : 'rotate-180'
                            }`} 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                        )}
                      </button>
                    ) : label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRowSkeleton key={index} />
                ))
              ) : sortedStudents.length > 0 ? (
                sortedStudents.map((student) => (
                  <tr key={student.id} className="hover:bg-gray-50 transition-colors duration-200">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleViewStudentDetails(student.id)}
                        className="text-indigo-600 hover:text-indigo-900 font-medium"
                        dir="auto"
                      >
                        {student.userName}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" dir="auto">
                        {student.gradeName ?? student.academicYear ?? '–'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600" dir="auto">
                      {student.governorate ?? '–'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 truncate max-w-[200px]" dir="ltr">
                        {student.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {student.phonenumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(student.registrationDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        student.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        <span className={`h-1.5 w-1.5 ml-1.5 rounded-full ${
                          student.status === 'active' ? 'bg-green-400' : 'bg-red-400'
                        }`}></span>
                        {student.status === 'active' ? 'نشط' : 'محظور'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {(canEditStudents || canDeleteStudents) ? (
                        <div className="flex items-center justify-end space-x-reverse space-x-3">
                          {canEditStudents && (
                          <button
                          onClick={() => setStudentToBlock(student)}
                          className={`group relative ${
                            student.status === 'active' 
                              ? 'text-yellow-500 hover:text-yellow-700'
                              : 'text-green-500 hover:text-green-700'
                          }`}
                        >
                          <LockClosedIcon className="w-5 h-5" />
                          <span className="sr-only">
                            {student.status === 'active' ? 'حظر' : 'تنشيط'} الطالب
                          </span>
                          <span className="absolute bottom-full left-0 mb-2 hidden group-hover:block w-24 px-2 py-1 text-xs text-center text-white bg-gray-900 rounded-md">
                            {student.status === 'active' ? 'حظر' : 'تنشيط'} الطالب
                          </span>
                        </button>
                          )}
                          {canDeleteStudents && (
                        <button
                          onClick={() => setStudentToDelete(student)}
                          className="group relative text-red-500 hover:text-red-700"
                          disabled={isLoading}
                        >
                          <TrashIcon className="w-5 h-5" />
                          <span className="sr-only">حذف الطالب</span>
                          <span className="absolute bottom-full left-0 mb-2 hidden group-hover:block w-24 px-2 py-1 text-xs text-center text-white bg-gray-900 rounded-md">
                            حذف الطالب
                          </span>
                        </button>
                          )}
                      </div>
                      ) : (
                        <span className="text-sm text-gray-400">عرض فقط</span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="px-6 py-10 text-center">
                    <div className="flex flex-col items-center">
                      <svg className="h-12 w-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                      <h3 className="mt-2 text-sm font-medium text-gray-900">لم يتم العثور على طلاب</h3>
                      <p className="mt-1 text-sm text-gray-500">حاول تعديل البحث أو التصفية للعثور على ما تبحث عنه.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!isLoading && totalPages > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="hidden sm:block">
              <p className="text-sm text-gray-700">
                {canViewStudentCounts ? (
                  <>
                    عرض <span className="font-medium">{totalCount > 0 ? (currentPage - 1) * pageSize + 1 : 0}</span> إلى{' '}
                    <span className="font-medium">{Math.min(currentPage * pageSize, totalCount || 0)}</span>{' '}
                    من <span className="font-medium">{(totalCount || 0).toLocaleString()}</span> نتيجة
                  </>
                ) : (
                  <>
                    الصفحة <span className="font-medium">{currentPage}</span>
                  </>
                )}
              </p>
            </div>
            <div className="flex justify-center sm:justify-end space-x-reverse space-x-1">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-2 py-2 rounded-l-md border text-sm font-medium ${
                  currentPage === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-500 hover:bg-gray-50'
                }`}
              >
                <span className="sr-only">الأول</span>
                <span>&laquo;</span>
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-2 py-2 border text-sm font-medium ${
                  currentPage === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-500 hover:bg-gray-50'
                }`}
              >
                <span className="sr-only">السابق</span>
                <span>&lsaquo;</span>
              </button>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(page => 
                  page === 1 || 
                  page === totalPages || 
                  (page >= currentPage - 1 && page <= currentPage + 1)
                )
                .map((page, index, array) => {
                  const prevPage = array[index - 1];
                  if (index > 0 && prevPage !== page - 1) {
                    return [
                      <span
                        key={`ellipsis-${prevPage}`}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                      >
                        ...
                      </span>,
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === page
                            ? 'z-10 bg-indigo-600 text-white border-indigo-600'
                            : 'bg-white text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ];
                  }
                  
                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        currentPage === page
                          ? 'z-10 bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                }).flat()}
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`relative inline-flex items-center px-2 py-2 border text-sm font-medium ${
                  currentPage === totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-500 hover:bg-gray-50'
                }`}
              >
                <span className="sr-only">التالي</span>
                <span>&rsaquo;</span>
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className={`relative inline-flex items-center px-2 py-2 rounded-r-md border text-sm font-medium ${
                  currentPage === totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-500 hover:bg-gray-50'
                }`}
              >
                <span className="sr-only">الأخير</span>
                <span>&raquo;</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Student Modal */}
      {showAddForm && (
        <div className="fixed inset-0 overflow-y-auto z-50">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500/75"></div>
            </div>
            
            <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto relative z-10">
              <h2 className="text-xl sm:text-2xl font-bold mb-6">إضافة طالب جديد</h2>
              {error && (
                <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  {/* Full Name */}
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      الاسم الكامل
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                      placeholder="أدخل اسم الطالب الكامل"
                      lang="ar"
                      dir="auto"
                    />
                  </div>

                  {/* Grade */}
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      الصف الدراسي
                    </label>
                    <select
                      name="gradeId"
                      value={formData.gradeId}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded-lg disabled:opacity-50"
                      required
                      disabled={isLoadingGrades}
                    >
                      <option value="">اختر الصف الدراسي</option>
                      {grades.map((g) => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Email Address */}
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      البريد الإلكتروني
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                      placeholder="student@example.com"
                      lang="ar"
                    />
                  </div>

                  {/* Phone Number */}
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      رقم هاتف الطالب
                    </label>
                    <input
                      type="tel"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                      placeholder="01xxxxxxxxx"
                      lang="ar"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      ‏11 رقمًا يبدأ بـ 01
                    </p>
                  </div>

                  {/* Parent Phone */}
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      رقم هاتف ولي الأمر
                    </label>
                    <input
                      type="tel"
                      name="parentPhone"
                      value={formData.parentPhone}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                      placeholder="01xxxxxxxxx"
                      lang="ar"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      ‏11 رقمًا يبدأ بـ 01
                    </p>
                  </div>

                  {/* Governorate (optional) */}
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      المحافظة <span className="text-gray-400 font-normal">(اختياري)</span>
                    </label>
                    <input
                      type="text"
                      name="governorate"
                      value={formData.governorate}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="مثال: القاهرة"
                      lang="ar"
                    />
                  </div>

                  {/* Center (optional) */}
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      المركز <span className="text-gray-400 font-normal">(اختياري)</span>
                    </label>
                    <input
                      type="text"
                      name="center"
                      value={formData.center}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="مثال: مدينة نصر"
                      lang="ar"
                    />
                  </div>

                  {/* Password */}
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      كلمة المرور
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                      minLength={6}
                      placeholder="••••••"
                      lang="ar"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      الحد الأدنى 6 أحرف
                    </p>
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      تأكيد كلمة المرور
                    </label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                      minLength={6}
                      placeholder="••••••"
                      lang="ar"
                    />
                  </div>
                </div>

                <div className="flex justify-start gap-4 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddForm(false);
                      setError(null);
                      setFormData({
                        name: '',
                        email: '',
                        password: '',
                        confirmPassword: '',
                        gradeId: '',
                        mobile: '',
                        parentPhone: '',
                        governorate: '',
                        center: '',
                      });
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                    disabled={isLoading}
                  >
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        جاري الإضافة...
                      </span>
                    ) : 'إضافة طالب'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {studentToDelete && (
        <div className="fixed inset-0 overflow-y-auto z-50">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={() => !isDeleting && setStudentToDelete(null)}>
              <div className="absolute inset-0 bg-gray-500/75"></div>
            </div>
            
            <div className="bg-white rounded-lg p-6 max-w-md w-full relative z-10">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <TrashIcon className="h-6 w-6 text-red-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:mr-4 sm:text-right">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    حذف الطالب
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      هل أنت متأكد من حذف {studentToDelete.userName}؟ لا يمكن التراجع عن هذا الإجراء.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row">
                <button
                  type="button"
                  onClick={() => handleDeleteStudent(studentToDelete.id)}
                  disabled={isDeleting}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {isDeleting ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      جاري الحذف...
                    </span>
                  ) : 'حذف'}
                </button>
                <button
                  type="button"
                  onClick={() => setStudentToDelete(null)}
                  disabled={isDeleting}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  إلغاء
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Success Modal */}
      {showDeleteSuccessModal && (
        <div className="fixed inset-0 overflow-y-auto z-50">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div
              className="fixed inset-0 transition-opacity"
              aria-hidden="true"
              onClick={() => {
                setShowDeleteSuccessModal(false);
                setDeletedStudentName(null);
              }}
            >
              <div className="absolute inset-0 bg-gray-500/75"></div>
            </div>

            <div className="bg-white rounded-lg p-6 max-w-md w-full relative z-10 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">تم الحذف بنجاح</h3>
              <p className="text-sm text-gray-500 mb-6">
                تم حذف {deletedStudentName || 'الطالب'} بنجاح.
              </p>
              <button
                type="button"
                onClick={() => {
                  setShowDeleteSuccessModal(false);
                  setDeletedStudentName(null);
                }}
                className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                حسناً
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Block/Unblock Student Modal */}
      {studentToBlock && (
        <div className="fixed inset-0 overflow-y-auto z-50">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={() => setStudentToBlock(null)}>
              <div className="absolute inset-0 bg-gray-500/75"></div>
            </div>
            
            <div className="bg-white rounded-lg p-6 max-w-md w-full relative z-10">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                  <LockClosedIcon className="h-6 w-6 text-yellow-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:mr-4 sm:text-right">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {studentToBlock.status === 'active' ? 'حظر الطالب' : 'تنشيط الطالب'}
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      هل أنت متأكد من {studentToBlock.status === 'active' ? 'حظر' : 'تنشيط'} {studentToBlock.userName}؟ 
                      {studentToBlock.status === 'active' 
                        ? ' لن يتمكن من الوصول إلى حسابه حتى يتم إعادة تنشيطه.'
                        : ' سيتمكن من الوصول إلى حسابه مرة أخرى.'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row">
                <button
                  type="button"
                  onClick={() => {
                    handleToggleStatus(studentToBlock);
                  }}
                  disabled={isBlocking}
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white sm:ml-3 sm:w-auto sm:text-sm ${
                    studentToBlock.status === 'active'
                      ? 'bg-yellow-600 hover:bg-yellow-700'
                      : 'bg-green-600 hover:bg-green-700'
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    studentToBlock.status === 'active'
                      ? 'focus:ring-yellow-500'
                      : 'focus:ring-green-500'
                  } disabled:opacity-50`}
                >
                  {isBlocking ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      جاري المعالجة...
                    </span>
                  ) : studentToBlock.status === 'active' ? 'حظر' : 'تنشيط'}
                </button>
                <button
                  type="button"
                  onClick={() => setStudentToBlock(null)}
                  disabled={isBlocking}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  إلغاء
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};
  


export default Students;